import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChefHat, Loader2, Package, Sparkles, ArrowRight } from 'lucide-react';
import { AKCENT, kc, ocekavanaPlatba } from '../RozvozCockpit/ui';

/**
 * K odvozu — hotové rozvozy rovnou na hlavní obrazovce rozvozce
 * (Cockpit 2.0, návrh 2026-10-19).
 *
 * Dřív tu byla jen karta „V teple čeká N" a objednávky se po jedné přidávaly
 * plusem až v Rozvoz Cockpitu. Teď je seznam tady: každá objednávka je papírový
 * lístek, ťuk ji vybere nebo odebere a „Vzít N" je předá serveru naráz
 * (`/api/rozvoz/vzit` bere pole). Cockpit se pak otevře rovnou na trase.
 *
 * Doporučená jízda (`doporuceno` ze serveru — nejdéle ležící + ty stejným
 * směrem) je předvybraná: rozvozce jen potvrdí. Když výběr změní ručně, další
 * obnova seznamu ho už nepřepíše.
 */
export default function KOdvozu({ hotove, variSe, doporuceno, limit, onVzato }) {
    const [vybrane, setVybrane] = useState(() => new Set(doporuceno));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const rucne = useRef(false);

    // Doporučení se mění s tím, co v teple přibude nebo ubude — dokud
    // rozvozce nesáhl na výběr, drží se ho. Vybrané, co z tepla zmizelo
    // (vzal si to kolega), se tiše odebere.
    const podpisDoporuceni = doporuceno.join(',');
    const podpisHotovych = hotove.map(o => o.id).join(',');
    useEffect(() => {
        setVybrane(s => {
            if (!rucne.current) return new Set(doporuceno);
            const ids = new Set(hotove.map(o => o.id));
            return new Set([...s].filter(id => ids.has(id)));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [podpisDoporuceni, podpisHotovych]);

    const prepnout = (id) => {
        rucne.current = true;
        setVybrane(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    };

    const vybraneObj = useMemo(() => hotove.filter(o => vybrane.has(o.id)), [hotove, vybrane]);
    const pocet = vybraneObj.length;
    const nadLimit = pocet > limit;
    const doporucenoPlati = doporuceno.length > 1 && !rucne.current;

    const vzit = async () => {
        if (!pocet || busy) return;
        setBusy(true);
        setErr(null);
        try {
            const r = await fetch('/api/rozvoz/vzit', {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ objednavky_ids: vybraneObj.map(o => o.id) }),
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.error || `Nepodařilo se vzít (HTTP ${r.status})`);
            rucne.current = false;
            onVzato?.();
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    const kam = vybraneObj.map(o => obec(o.adresa)).filter(Boolean);

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 16px 10px' }}>
                <h2 style={{ margin: 0, fontFamily: 'var(--pismo-display)', fontWeight: 400, fontSize: 24, letterSpacing: '0.01em', textTransform: 'uppercase' }}>K odvozu</h2>
                <span style={{ fontSize: 13, fontWeight: 800, color: AKCENT }}>{hotove.length} {hotove.length === 1 ? 'hotová' : hotove.length < 5 ? 'hotové' : 'hotových'}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>vyber, co bereš</span>
            </div>

            <div style={{
                flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 10,
            }}>
                {doporucenoPlati && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14,
                        background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-default)',
                        fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                    }}>
                        <Sparkles size={17} style={{ color: AKCENT, flexShrink: 0 }} />
                        <span><b>Doporučeno {doporuceno.map(id => `#${hotove.find(o => o.id === id)?.cislo ?? ''}`).join(' + ')}</b> · stejný směr, vybráno za tebe</span>
                    </div>
                )}

                {hotove.map(o => (
                    <Listek key={o.id} o={o} vybrano={vybrane.has(o.id)} onClick={() => prepnout(o.id)} disabled={busy} />
                ))}

                {variSe.length > 0 && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12,
                        background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-default)',
                        fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
                    }}>
                        <ChefHat size={16} style={{ color: '#F59E0B', flexShrink: 0 }} />
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Za chvíli hotové: <b style={{ color: 'var(--text-primary)' }}>{variSe.map(o => `#${o.cislo}${o.adresa ? ' ' + obec(o.adresa) : ''}`).join(', ')}</b>
                        </span>
                    </div>
                )}
                {err && <div style={{ fontSize: 13, fontWeight: 700, color: '#EF4444' }}>{err}</div>}
            </div>

            <div style={{ flexShrink: 0, padding: '8px 16px 10px' }}>
                <button type="button" onClick={vzit} disabled={!pocet || busy} style={{
                    width: '100%', minHeight: 64, borderRadius: 18, border: 'none',
                    background: pocet ? AKCENT : 'var(--surface-elevated)', color: pocet ? '#140A00' : 'var(--text-muted)',
                    boxShadow: pocet ? '0 10px 30px rgba(255,138,0,0.30)' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    font: 'inherit', cursor: pocet && !busy ? 'pointer' : 'default', padding: '6px 16px',
                    WebkitTapHighlightColor: 'transparent',
                }}>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                        <span style={{ fontSize: 18, fontWeight: 800 }}>
                            {pocet ? `Vzít ${pocet} ${pocet === 1 ? 'objednávku' : pocet < 5 ? 'objednávky' : 'objednávek'}` : 'Vyber objednávky'}
                        </span>
                        {pocet > 0 && (
                            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.75, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {nadLimit ? `víc než ${limit} v jedné jízdě` : kam.join(' · ')}
                            </span>
                        )}
                    </span>
                    {busy ? <Loader2 size={22} className="animate-spin" /> : pocet > 0 && <ArrowRight size={22} strokeWidth={2.6} />}
                </button>
            </div>
        </div>
    );
}

/** Objednávka jako papírový lístek — ťuk vybere / odebere. */
function Listek({ o, vybrano, onClick, disabled }) {
    const lezi = o.hotovo_v ? Math.max(0, Math.round((Date.now() - new Date(o.hotovo_v).getTime()) / 60000)) : null;
    const dlouho = lezi != null && lezi >= 10;
    const platba = o.zaplaceno
        ? { text: 'zaplaceno', barva: 'var(--papir-ok)', pozadi: 'rgba(4,120,87,0.10)' }
        : ocekavanaPlatba(o) === 'kartou'
            ? { text: `kartou ${kc(o.castka_kc)}`, barva: 'var(--papir-info)', pozadi: 'rgba(29,78,216,0.10)' }
            : { text: `hotově ${kc(o.castka_kc)}`, barva: 'var(--brand)', pozadi: 'rgba(163,75,0,0.10)' };

    return (
        <button type="button" onClick={onClick} disabled={disabled} aria-pressed={vybrano} className="papir listek" style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14,
            outline: vybrano ? `2.5px solid ${AKCENT}` : 'none', outlineOffset: -1,
            font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%',
            WebkitTapHighlightColor: 'transparent',
        }}>
            <span style={{
                width: 30, height: 30, borderRadius: 15, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: vybrano ? AKCENT : 'transparent',
                border: `2px solid ${vybrano ? AKCENT : 'var(--border-strong)'}`,
            }}>{vybrano && <Check size={17} strokeWidth={3.2} color="#fff" />}</span>

            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 9, minWidth: 0 }}>
                    <span style={{ fontFamily: 'var(--pismo-display)', fontSize: 26, lineHeight: 1, flexShrink: 0 }}>#{o.cislo}</span>
                    <span style={{ fontSize: 15.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.adresa || 'bez adresy'}</span>
                </span>
                <span style={{ display: 'flex', gap: 6, marginTop: 4, fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                    <span style={{ color: dlouho ? 'var(--papir-chyba)' : undefined }}>
                        {lezi == null ? 'hotová' : lezi < 1 ? 'hotová teď' : <>leží <b style={{ color: dlouho ? 'var(--papir-chyba)' : 'var(--text-primary)' }}>{lezi} min</b></>}
                    </span>
                    {o.cas_slibeny && <span>· slíbeno <b style={{ color: 'var(--text-primary)' }}>{String(o.cas_slibeny).slice(0, 5)}</b></span>}
                </span>
            </span>

            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 800, padding: '3px 8px', borderRadius: 7, background: platba.pozadi, color: platba.barva, whiteSpace: 'nowrap' }}>{platba.text}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    <Package size={13} />{Math.max(1, o.krabic || 0)}{o.jizda_min != null ? ` · ${o.jizda_min} min` : ''}
                </span>
            </span>
        </button>
    );
}

/** „Čeladná 890" → „Čeladná", „Markova 929, Frenštát" → „Frenštát" — kam se jede. */
function obec(adresa) {
    if (!adresa) return null;
    const casti = String(adresa).split(',').map(s => s.trim()).filter(Boolean);
    const posledni = casti[casti.length - 1] ?? '';
    return posledni.replace(/\s*\d+[a-zA-Z/]*\s*$/, '').replace(/^\d{3}\s?\d{2}\s*/, '') || posledni;
}
